/**
 * The delivery window a client asked for, and whether this load is keeping it.
 *
 * V-99. A site foreman submits "48 t, 2026-08-29, between 08:00 and 12:00 —
 * we pour concrete at 12:30". The dispatcher approves. Reproduced on a live
 * server 2026-08-27 11:59 UTC: both loads created carried
 * scheduled_for='2026-08-29T08:00:00' and a note reading "Pristatymo langas:
 * 08:00–12:00", and that was the whole of it. `scheduled_for` appeared in no
 * screen in the product — nothing in frontend/src referenced it at all — and
 * the window's END existed only inside that Lithuanian sentence, on a product
 * that also runs in English. So the promise was collected, stored, and never
 * shown to either of the two people who could still keep it.
 *
 * WHY THE VERDICT IS COMPUTED HERE AND NOT ON THE SERVER. The window is local
 * wall-clock: a client types 'HH:MM' off the clock at their own site, and this
 * product stores no timezone for a tenant. `delivered_at` is UTC. Comparing
 * them needs an offset the server does not have and the browser does — the
 * same offset lib/format.js's formatOrderDateTime() has always used to render
 * any time at all.
 *
 * The same offset, NOT the same exposure, and V-103 sharpened this after the
 * first wording blurred them. format.js uses the browser's zone to RENDER a
 * true instant: a wrong zone puts a visibly wrong clock time on screen, and
 * somebody reconciling against a paper slip sees it. This module uses it to
 * DECIDE: a wrong zone turns `late` into `kept` silently, with nothing on any
 * screen to show for it. So the assumption is stated here rather than
 * inherited — these verdicts are right when the reader's device is set to the
 * carrier's own time, which for a Lithuanian haulier's dispatcher and drivers
 * it is, and a driver whose phone has followed him across a border into CEST
 * will read the window an hour out.
 */

/**
 * How close to the end of the window still counts as "act now".
 *
 * One number for both screens, not one per screen. A dispatcher chasing a
 * driver whose own phone says everything is fine — or the reverse — is how
 * both surfaces stop being believed, and the first thing an untrusted warning
 * costs is the hour somebody would have acted in.
 */
export const CLOSING_WITHIN_MINUTES = 60;

/**
 * Local wall-clock text -> Date, or null.
 *
 * A bare 'YYYY-MM-DDTHH:MM:SS' with no zone is read by every browser as local
 * time, which is exactly what this value means. A date with no time at all
 * ('2026-08-29', what scheduled_for holds when the client stated no window)
 * is NOT a window and must not become midnight — a load would then be "late"
 * from one second past midnight on the day it was ordered for.
 */
function localTime(value) {
  if (typeof value !== 'string' || !value.includes('T')) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A real instant — what delivered_at is — from any shape this database has
 * ever written.
 *
 * The `.replace(' ', 'T')` + 'Z' branch is lifted from lib/format.js's
 * toDate(), and V-103 restored it here after leaving it out. That file keeps
 * it with the reason spelled out: SQLite's older space-separated shape has
 * no zone, Chrome reads such a string as LOCAL time, and a delivery at
 * 09:15Z then reads as 09:15 local — three hours adrift in Lithuania in
 * summer. In format.js that mis-renders a clock, which somebody notices
 * here it would flip a VERDICT: a load delivered 15 minutes late would be
 * reported as kept, with nothing on screen to show for it. Reachable from an
 * un-migrated database or from a response held in the worker's 24-hour API
 * cache.
 */
function instant(value) {
  if (!value) return null;
  const normalized = String(value).includes('T') ? value : `${String(value).replace(' ', 'T')}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * `{ start, end, state, minutesLeft, minutesLate }` for a load that carries a
 * promise, or null for one that does not.
 *
 * States, and what each is for:
 *   'kept'    — delivered inside the window. The number a carrier can show a
 *               client at renewal time.
 *   'late'    — delivered after it. Also the number, and the honest half.
 *   'early'   — delivered before it. Reported, NOT coloured as a fault: a
 *               truck arriving at 06:30 for an 08:00 window is a site that
 *               may not be ready to receive, which is worth knowing and is
 *               nobody's failure.
 *   'missed'  — not delivered and the window has closed. Nothing left to
 *               save; the client is already waiting.
 *   'will_miss' — not delivered, the window is still OPEN, and the live ETA
 *               puts arrival after it closes. The one state that is a
 *               forecast rather than an observation, and the only one a
 *               dispatcher can still act on with time to spare: reassign,
 *               reorder the day, or ring the client while it is still news
 *               rather than an apology.
 *   'closing' — not delivered, inside the last CLOSING_WITHIN_MINUTES. Says
 *               the window is nearly up; says nothing about whether the
 *               truck will make it, which is what 'will_miss' adds.
 *   'open'    — not delivered, and there is still time.
 *
 * A cancelled load has no promise to keep and returns null: showing "missed"
 * against work nobody was going to do would put a fault in a report that
 * belongs in no report at all.
 *
 * `eta` is one entry from GET /api/orders/live-eta — {eta_at, eta_source}.
 * Optional, and every caller that omits it gets exactly the verdicts this
 * function has always returned. See willMiss() below for the one rule that
 * governs when a forecast is allowed to raise an alarm.
 */
export function deliveryWindow(order, now = new Date(), eta = null) {
  if (!order || order.order_status === 'cancelled') return null;

  const start = localTime(order.scheduled_for);
  const end = localTime(order.scheduled_until);
  // Both ends together or neither: core/delivery_window.py::validate()
  // refuses a half-open window, and since V-351 it does so for the
  // dispatcher's own order form as well as the client portal — so one end
  // alone means this load carries no promise, whichever door it came in by.
  if (!start || !end) return null;

  const delivered = instant(order.delivered_at);
  if (delivered) {
    if (delivered > end) {
      return { start, end, state: 'late', minutesLate: minutesBetween(end, delivered) };
    }
    if (delivered < start) {
      return { start, end, state: 'early', minutesEarly: minutesBetween(delivered, start) };
    }
    return { start, end, state: 'kept' };
  }

  if (now > end) return { start, end, state: 'missed', minutesLate: minutesBetween(end, now) };

  const minutesLeft = minutesBetween(now, end);

  // Checked BEFORE 'closing', because it is strictly the more specific
  // claim: "the window is nearly up" and "this truck will not make it" are
  // different sentences, and a dispatcher who is told the second one does
  // not need the first.
  const predictedLate = willMiss(eta, end);
  if (predictedLate !== null) {
    return { start, end, state: 'will_miss', minutesLeft,
             minutesLate: predictedLate, etaSource: eta.eta_source };
  }

  return {
    start,
    end,
    state: minutesLeft <= CLOSING_WITHIN_MINUTES ? 'closing' : 'open',
    minutesLeft,
  };
}

/**
 * Minutes this load is forecast to overshoot its window, or null when no
 * honest forecast can be made.
 *
 * NOT FORECAST FROM A STALE FIX, and this is the load-bearing rule. The live
 * ETA still shows a number for a truck whose phone went quiet hours ago —
 * "12 km, last GPS 2 h ago" beats silence on a screen that is merely
 * reporting. But raising an alarm is a stronger act than reporting: it asks
 * a dispatcher to ring a client and rearrange a day, and doing that on a
 * position that may be two hours out of date is how a warning system gets
 * switched off. The rest of this product already draws exactly this line —
 * core/smart_match.py and core/auto_dispatch.py both REFUSE to decide
 * anything from a fix past POSITION_MAX_AGE_MINUTES, while screens that only
 * display one say so and carry on.
 *
 * A straight-line 'estimated' ETA IS allowed to warn. It is a weaker number
 * and the caller marks it as one, but it is a number about where the truck
 * actually is — and a warning that fired only when the routing service was
 * healthy would be missing on precisely the days it is most needed.
 */
export function willMiss(eta, end) {
  if (!eta || !eta.eta_at || eta.eta_source === 'stale') return null;
  const arrival = instant(eta.eta_at);
  if (!arrival || arrival <= end) return null;
  return minutesBetween(end, arrival);
}

/**
 * Whole minutes, rounded UP.
 *
 * Rounding down would print "0 min left" while there are still 40 seconds to
 * drive in — a number that reads as "too late, don't bother" to the one
 * person who could still make it. Every caller above has already established
 * which of the two instants is the later one, so this needs no floor at zero
 * and does not pretend to have one.
 */
function minutesBetween(from, to) {
  return Math.ceil((to.getTime() - from.getTime()) / 60000);
}

/** 'HH:MM–HH:MM' in the reader's own clock, which is the carrier's. */
export function windowLabel(window) {
  if (!window) return '';
  return `${clock(window.start)}–${clock(window.end)}`;
}

function clock(date) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
